import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Trash2, Save, X, Edit3, MessageSquareQuote } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";

const empty = () => ({ name: "", role: "", body: "", rating: 5, display_order: 0, active: true });

function Editor({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || empty());
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="card-swiss p-5 space-y-3" data-testid="testimonial-editor">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input placeholder="Client name *" value={f.name} onChange={(e) => set("name", e.target.value)}
          className="md:col-span-2 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="t-name-input" />
        <input placeholder="Role / Company" value={f.role} onChange={(e) => set("role", e.target.value)}
          className="md:col-span-2 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="t-role-input" />
        <select value={f.rating} onChange={(e) => set("rating", Number(e.target.value))}
          className="border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none">
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{"★".repeat(n)}{"☆".repeat(5 - n)}</option>)}
        </select>
        <input type="number" placeholder="Order (0 first)" value={f.display_order}
          onChange={(e) => set("display_order", Number(e.target.value))}
          className="border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="t-order-input" />
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="t-active-toggle" />
          Show on website
        </label>
      </div>
      <textarea rows={3} placeholder="Testimonial (2–3 sentences)" value={f.body}
        onChange={(e) => set("body", e.target.value)}
        className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="t-body-input" />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-swiss btn-outline"><X size={14} /> Cancel</button>
        <button onClick={() => onSave(f)} disabled={saving || !f.name || !f.body}
          className="btn-swiss btn-accent" data-testid="t-save-btn">
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function Testimonials() {
  usePageTitle("Testimonials");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // {id?} + fields
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/testimonials").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    setErr("");
    setSaving(true);
    try {
      if (editing && editing.id) await api.put(`/testimonials/${editing.id}`, t);
      else await api.post("/testimonials", t);
      setEditing(null); setCreating(false);
      load();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/testimonials/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="testimonials-root">
      <div className="border-b border-[hsl(var(--border))] pb-6 flex items-end justify-between">
        <div>
          <div className="overline mb-2">Content</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Testimonials</h1>
          <p className="text-sm text-muted-foreground mt-2">Client quotes shown on the Home page below the client logo strip.</p>
        </div>
        {!creating && !editing && (
          <button onClick={() => setCreating(true)} className="btn-swiss btn-accent" data-testid="new-testimonial-btn">
            <Plus size={14} /> Add Testimonial
          </button>
        )}
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {creating && (
        <Editor initial={empty()} saving={saving} onCancel={() => setCreating(false)} onSave={save} />
      )}

      <div className="space-y-3">
        {rows.map((r) => (
          editing?.id === r.id ? (
            <Editor key={r.id} initial={r} saving={saving} onCancel={() => setEditing(null)} onSave={save} />
          ) : (
            <div key={r.id} className="card-swiss p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`t-row-${r.id}`}>
              <div className="md:col-span-1 text-center">
                <MessageSquareQuote size={24} className="mx-auto text-[#FF6B00]" />
                <div className="text-xs text-muted-foreground mt-1">#{r.display_order}</div>
              </div>
              <div className="md:col-span-8">
                <div className="text-[#FF6B00] text-sm mb-2">{"★".repeat(r.rating || 5)}</div>
                <div className="text-sm">"{r.body}"</div>
                <div className="mt-2">
                  <div className="font-bold text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.role}</div>
                </div>
              </div>
              <div className="md:col-span-3 flex flex-col md:items-end gap-2">
                <span className={`inline-flex items-center gap-2 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${r.active ? "border-emerald-500 text-emerald-600" : "border-zinc-400 text-zinc-500"}`}>
                  {r.active ? "Live" : "Hidden"}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(r)} className="btn-swiss btn-outline text-xs" data-testid={`edit-t-${r.id}`}>
                    <Edit3 size={12} /> Edit
                  </button>
                  <button onClick={() => remove(r.id)} className="btn-swiss btn-outline text-xs text-red-500 border-red-500" data-testid={`delete-t-${r.id}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          )
        ))}
        {rows.length === 0 && !creating && (
          <div className="card-swiss p-8 text-center text-muted-foreground text-sm">
            No testimonials yet. Add your first one to display on the website.
          </div>
        )}
      </div>
    </div>
  );
}
